import { Thermometer, TrendingUp, TrendingDown, Minus, Users, AlertTriangle } from 'lucide-react';
import { Theme } from '@/shared/constants/themes';

interface LiveStatsProps {
    stats: {
        thermal: string;
        trend: string;
        audience: string;
        signals: string[];
    } | null;
    theme?: Theme;
}

export const LiveStats: React.FC<LiveStatsProps> = ({ stats, theme }) => {
    const themeColors = theme?.colors;

    // Don't render if no data available
    if (!stats) {
        return (
            <div 
                className="rounded-lg shadow-sm border p-4"
                style={{
                    backgroundColor: themeColors?.surface || '#FFFFFF',
                    borderColor: themeColors?.border || '#E5E7EB'
                }}
            >
                <div className="flex items-center gap-2 mb-3">
                    <Thermometer 
                        size={16} 
                        style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                    />
                    <h3 
                        className="text-sm font-semibold"
                        style={{ color: themeColors?.foreground || '#374151' }}
                    >
                        Thermique du Live
                    </h3>
                </div>
                <p 
                    className="text-xs italic text-center py-6"
                    style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                >
                    En attente de données...
                </p>
            </div>
        );
    }

    const getThermalConfig = (status: string) => {
        switch (status) {
            case 'hot': return { color: 'bg-gradient-to-r from-orange-500 to-red-500', icon: '🔥', label: 'Hot', desc: 'Forte interaction' };
            case 'normal': return { color: 'bg-gradient-to-r from-blue-400 to-blue-500', icon: '✨', label: 'Normal', desc: 'Stable' };
            case 'cold': return { color: 'bg-gradient-to-r from-blue-600 to-indigo-600', icon: '❄️', label: 'Cold', desc: 'Peu d\'interactions' };
            case 'silence': return { color: 'bg-gradient-to-r from-gray-500 to-gray-600', icon: '🔇', label: 'Silence', desc: 'Chat très calme' };
            case 'dangerous_drop': return { color: 'bg-gradient-to-r from-red-600 to-red-700', icon: '🚨', label: 'Alerte', desc: 'Chute dangereuse' };
            default: return { color: 'bg-gradient-to-r from-gray-400 to-gray-500', icon: '⚪', label: 'Inconnu', desc: '' };
        }
    };

    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case 'rising': return <TrendingUp size={14} style={{ color: themeColors?.success || '#10B981' }} />;
            case 'drop': case 'crash': return <TrendingDown size={14} style={{ color: themeColors?.error || '#EF4444' }} />;
            default: return <Minus size={14} style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }} />;
        }
    };

    const thermal = getThermalConfig(stats.thermal);

    return (
        <div 
            className="rounded-lg shadow-sm border overflow-hidden"
            style={{
                backgroundColor: themeColors?.surface || '#FFFFFF',
                borderColor: themeColors?.border || '#E5E7EB'
            }}
        >
            {/* Header */}
            <div 
                className="px-4 py-3 border-b"
                style={{ borderBottomColor: themeColors?.borderLight || '#F3F4F6' }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Thermometer 
                            size={16} 
                            style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                        />
                        <h3 
                            className="text-sm font-semibold"
                            style={{ color: themeColors?.foreground || '#374151' }}
                        >
                            Thermique
                        </h3>
                    </div>
                </div>
            </div>

            {/* Thermal Status */}
            <div className="p-4">
                <div className={`${thermal.color} text-white rounded-lg p-3 mb-3`}>
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-2xl">{thermal.icon}</span>
                        <div className="text-right">
                            <div className="text-sm font-bold">{thermal.label}</div>
                            <div className="text-xs opacity-90">{thermal.desc}</div>
                        </div>
                    </div>
                </div>

                {/* Trend & Audience */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div 
                        className="rounded-md p-2"
                        style={{ backgroundColor: themeColors?.backgroundTertiary || '#F3F4F6' }}
                    >
                        <div className="flex items-center gap-1 mb-0.5">
                            {getTrendIcon(stats.trend)}
                            <span 
                                className="text-xs font-medium"
                                style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                            >
                                Tendance
                            </span>
                        </div>
                        <div 
                            className="text-xs font-semibold capitalize"
                            style={{ color: themeColors?.foreground || '#111827' }}
                        >
                            {stats.trend}
                        </div>
                    </div>

                    <div 
                        className="rounded-md p-2"
                        style={{ backgroundColor: themeColors?.backgroundTertiary || '#F3F4F6' }}
                    >
                        <div className="flex items-center gap-1 mb-0.5">
                            <Users 
                                size={14} 
                                style={{ color: themeColors?.primary || '#9333EA' }}
                            />
                            <span 
                                className="text-xs font-medium"
                                style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                            >
                                Audience
                            </span>
                        </div>
                        <div 
                            className="text-xs font-semibold capitalize"
                            style={{ color: themeColors?.foreground || '#111827' }}
                        >
                            {stats.audience === 'mostly_new' ? 'Nouveaux' : stats.audience === 'mostly_regular' ? 'Réguliers' : 'Mixte'}
                        </div>
                    </div>
                </div>

                {/* Signals */}
                {stats.signals.length > 0 && (
                    <div className="space-y-1.5">
                        {stats.signals.map((signal, idx) => (
                            <div 
                                key={idx} 
                                className="flex items-start gap-2 border rounded-md p-2"
                                style={{
                                    backgroundColor: themeColors?.warningLight || '#FEF3C7',
                                    borderColor: themeColors?.warning || '#F59E0B'
                                }}
                            >
                                <AlertTriangle 
                                    size={12} 
                                    className="mt-0.5 flex-shrink-0"
                                    style={{ color: themeColors?.warning || '#D97706' }}
                                />
                                <span 
                                    className="text-xs font-medium leading-tight"
                                    style={{ color: themeColors?.warningDark || '#92400E' }}
                                >
                                    {signal}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
